/**
 * SEO: dynamic <title> and <h1> for exam and lesson pages.
 * Used for /exams/:examSlug (hub, simulator) and /exams/:examSlug/lessons.
 * All exams (telc-b1, goethe-b2, osd-c1, etc.) and supported languages.
 */
import { parseExamSlug, isValidExamSlug } from './examSlug';

const BRAND = ' | Logami';

/** Display name for provider (for h1/title). */
function getProviderDisplayName(providerKey) {
  const names = { telc: 'TELC', goethe: 'Goethe', osd: 'ÖSD' };
  return names[providerKey] || providerKey.toUpperCase();
}

/** e.g. "TELC B1", "Goethe B2", "ÖSD C1" */
export function getExamDisplayName(slug) {
  const parsed = parseExamSlug(slug);
  if (!parsed) return '';
  const providerKey = parsed.provider.toLowerCase();
  return `${getProviderDisplayName(providerKey)} ${parsed.level}`;
}

/** Title templates: (examDisplayName) => full title. */
const TITLES = {
  en: {
    hub: (name) => `${name} Mock Exam – Practice & Prep${BRAND}`,
    simulator: (name) => `${name} Full Practice Test${BRAND}`,
    lessons: (name) => `Tips & Strategy to Pass ${name}${BRAND}`,
  },
  de: {
    hub: (name) => `${name} Übungstest – Online üben${BRAND}`,
    simulator: (name) => `${name} Prüfungssimulation${BRAND}`,
    lessons: (name) => `Tipps & Strategie: ${name} bestehen${BRAND}`,
  },
  fr: {
    hub: (name) => `Examen blanc ${name} – S'entraîner${BRAND}`,
    simulator: (name) => `Test complet ${name}${BRAND}`,
    lessons: (name) => `Conseils et stratégie pour réussir ${name}${BRAND}`,
  },
  es: {
    hub: (name) => `Examen de prueba ${name} – Practicar${BRAND}`,
    simulator: (name) => `Examen completo ${name}${BRAND}`,
    lessons: (name) => `Consejos y estrategia para aprobar ${name}${BRAND}`,
  },
  tr: {
    hub: (name) => `${name} Deneme Sınavı – Pratik${BRAND}`,
    simulator: (name) => `${name} Tam Uygulama Sınavı${BRAND}`,
    lessons: (name) => `${name} geçmek için ipuçları ve strateji${BRAND}`,
  },
  ru: {
    hub: (name) => `Пробный экзамен ${name} – Тренировка${BRAND}`,
    simulator: (name) => `Полный пробный тест ${name}${BRAND}`,
    lessons: (name) => `Советы и стратегия: сдать ${name}${BRAND}`,
  },
};

const H1 = {
  en: {
    hub: (name) => `${name} Mock Exam`,
    simulator: (name) => `${name} Full Practice Test`,
    lessons: (name) => `Tips & Strategy to Pass ${name}`,
  },
  de: {
    hub: (name) => `${name} Übungstest`,
    simulator: (name) => `${name} Prüfungssimulation`,
    lessons: (name) => `Tipps & Strategie: ${name} bestehen`,
  },
  fr: {
    hub: (name) => `Examen blanc ${name}`,
    simulator: (name) => `Test complet ${name}`,
    lessons: (name) => `Conseils pour réussir ${name}`,
  },
  es: {
    hub: (name) => `Examen de prueba ${name}`,
    simulator: (name) => `Examen completo ${name}`,
    lessons: (name) => `Consejos para aprobar ${name}`,
  },
  tr: {
    hub: (name) => `${name} Deneme Sınavı`,
    simulator: (name) => `${name} Tam Uygulama Sınavı`,
    lessons: (name) => `${name} geçmek için ipuçları`,
  },
  ru: {
    hub: (name) => `Пробный экзамен ${name}`,
    simulator: (name) => `Полный тест ${name}`,
    lessons: (name) => `Советы: сдать ${name}`,
  },
};

const LANGS = ['en', 'de', 'fr', 'es', 'tr', 'ru'];
const defaultLang = 'en';

function lang(l) {
  return l && LANGS.includes(l.toLowerCase()) ? l.toLowerCase() : defaultLang;
}

/**
 * Page <title> for exam hub or simulator.
 * @param {string} slug - e.g. 'telc-b1'
 * @param {string} language - e.g. 'en'
 * @param {'hub'|'simulator'} variant
 */
export function getExamTitle(slug, language, variant) {
  if (!isValidExamSlug(slug)) return '';
  const name = getExamDisplayName(slug);
  const templates = TITLES[lang(language)] || TITLES[defaultLang];
  const fn = templates[variant] || templates.hub;
  return fn(name);
}

/**
 * Main <h1> text for exam hub or simulator.
 */
export function getExamH1(slug, language, variant) {
  if (!isValidExamSlug(slug)) return '';
  const name = getExamDisplayName(slug);
  const templates = H1[lang(language)] || H1[defaultLang];
  const fn = templates[variant] || templates.hub;
  return fn(name);
}

/**
 * Page <title> for lessons / strategy page (how to pass X).
 */
export function getLessonTitle(slug, language) {
  if (!isValidExamSlug(slug)) return '';
  const name = getExamDisplayName(slug);
  const templates = TITLES[lang(language)] || TITLES[defaultLang];
  return (templates.lessons && templates.lessons(name)) || `${name} – Lessons${BRAND}`;
}

/**
 * Main <h1> for lessons / strategy page.
 */
export function getLessonH1(slug, language) {
  if (!isValidExamSlug(slug)) return '';
  const name = getExamDisplayName(slug);
  const templates = H1[lang(language)] || H1[defaultLang];
  return (templates.lessons && templates.lessons(name)) || `Tips to Pass ${name}`;
}
